import { useCallback, useEffect, useState } from 'react';
import { FormInstance } from 'antd';
import { Attribute, ImagePayload, ProductSalePrice, Unit } from 'types';
import {
  useCreateCodeMutation,
  useGetAllAttributesQuery,
  useGetAllBrandsQuery,
  useGetAllCategoriesQuery,
  useGetAllUnitsQuery,
  useGetStatusQuery,
  useGetSuppliersQuery,
} from 'services';
import { useWatch } from 'antd/es/form/Form';
import { generateRandomBarcode } from 'utils';
import { Brand, ProductVersion } from 'models';

interface UseManageProductProps {
  form: FormInstance;
  isDetail?: boolean;
  defaultIsEdit?: boolean;
  initialProductUnits?: Unit[];
  initialProductAttributes?: Attribute[];
  initialProductSalePrices?: ProductSalePrice[];
  initialProductSuppliers?: string[];
}

export const useManageProduct = ({
  form,
  isDetail = false,
  defaultIsEdit = true,
  initialProductUnits = [],
  initialProductAttributes = [],
  initialProductSalePrices = [],
  initialProductSuppliers = [],
}: UseManageProductProps) => {
  // State management
  const [checkActive, setCheckActive] = useState<boolean>(true);
  const [checkInactive, setInactive] = useState<boolean>(false);
  const [checkUpdate, setCheckUpdate] = useState<boolean>(false);
  const [barcode, setBarcode] = useState<string>('');
  const [listVersion, setListVersion] = useState<ProductVersion[]>([]);
  const [productUnits, setProductUnits] = useState<Unit[]>(initialProductUnits);
  const [productAttributes, setProductAttributes] = useState<Attribute[]>(initialProductAttributes);
  const [productSalePrices, setProductSalePrices] = useState<ProductSalePrice[]>(initialProductSalePrices);
  const [productSuppliers, setProductSuppliers] = useState<string[]>(initialProductSuppliers);
  const [modalOpen, setModalOpen] = useState(false);
  const [version, setVersion] = useState(form.getFieldValue('version') || 1);
  const [isEdit, setIsEdit] = useState(defaultIsEdit);
  const [statusSave, setStatusSave] = useState<string>('bản nháp');
  const [imagesData, setImagesData] = useState<ImagePayload[]>([]);
  const [filteredBrands, setFilteredBrands] = useState<Brand[]>([]);
  const [prefixCode, setPrefixCode] = useState<string>('');
  // API Queries
  const { data: suppliers } = useGetSuppliersQuery({
    query: {
      size: 'unlimited',
      filter: 'status.isActive:eq:true',
      include: 'baseUser|status',
    },
  });

  const { data: brands, isLoading: isLoadingBrands } = useGetAllBrandsQuery({
    query: {
      size: 'unlimited',
      filter: 'isActive:eq:true',
      include: 'categoryBrands.category',
    },
  });

  const { data: categories, isLoading: isLoadingCategories } = useGetAllCategoriesQuery({
    query: {
      size: 'unlimited',
      filter: 'isActive:eq:true',
      include: 'categoryBrands.brand|categoryAttributes.attribute|categoryUnits.unit|productDimensions|sizeSpecs',
    },
  });

  const { data: units } = useGetAllUnitsQuery({
    query: {
      size: 'unlimited',
      filter: 'isActive:eq:true&&isCustom:eq:false',
    },
  });

  const { data: attributes } = useGetAllAttributesQuery({
    query: {
      size: 'unlimited',
      filter: 'isActive:eq:true',
    },
  });

  const { data: statuses } = useGetStatusQuery({
    query: { filter: 'type:eq:product' },
  });

  const [createcode, { data: code }] = useCreateCodeMutation();

  // Form watching
  const fieldAttrs = useWatch('productAttributes', form);
  const fieldProductImages = useWatch('productImages', form);
  // Status IDs
  const isPublishId = statuses?.data?.find((status) => status.name === 'Active')?.id;
  const isDraftId = statuses?.data?.find((status) => status.name === 'Draft')?.id;
  const isInactiveId = statuses?.data?.find((status) => status.name === 'Inactive')?.id;

  // Handlers
  const handleBarcode = useCallback(() => {
    const randomBarcode = generateRandomBarcode();
    setBarcode(randomBarcode);
    form.setFieldValue('barcode', randomBarcode);
  }, [form]);

  const handleCreatecode = useCallback(async () => {
    try {
      const res = await createcode({ code: 'SP' }).unwrap();
      if (res) {
        form.setFieldValue('code', res);
        setPrefixCode(res.substring(0, 6));
      } else {
        console.error('Barcode creation failed, no data returned.');
      }
    } catch (err) {
      console.error('Barcode creation failed:', err);
    }
  }, [createcode, form]);
  const getBrandsByCategoryIds = useCallback(
    (categoryIds: string) => {
      const category = categories?.data?.rows.find((cat) => cat.id === categoryIds);
      const relatedBrands =
        category?.categoryBrands?.filter((cb) => cb.brand && cb.brand.isActive).map((cb) => cb.brand) || [];
      // Not duplicate data
      const brandsArray = relatedBrands.filter(
        (brand, index, self) => self.findIndex((b) => b?.id === brand?.id) === index,
      );
      return brandsArray as Brand[];
    },
    [categories?.data?.rows],
  );
  const handleCategoryChange = useCallback(
    (categoryId: string | null) => {
      if (!categoryId) {
        setFilteredBrands([]);
        return;
      }

      const category = categories?.data?.rows.find((cat) => cat.id === categoryId);
      const relatedBrands =
        category?.categoryBrands?.filter((cb) => cb.brand && cb.brand.isActive).map((cb) => cb.brand) || [];
      // Not duplicate data
      const brandsArray = relatedBrands.filter(
        (brand, index, self) => self.findIndex((b) => b?.id === brand?.id) === index,
      );
      setFilteredBrands(brandsArray as Brand[]);

      if (category && !isDetail) {
        form.setFieldsValue({
          dimension: undefined,
          sizeSpec: undefined,
          color: undefined,
        });

        // Handle category attributes
        if (category.categoryAttributes && category.categoryAttributes.length > 0) {
          const attributes = category.categoryAttributes
            .filter((attr) => attr.isActive && attr.attribute)
            .map((attr, order) => ({
              attributeId: attr?.attribute?.id,
              title: attr?.attribute?.name || '',
              salePrice: 0,
              costPrice: 0,
              note: '',
              order: order + 1,
            }));

          setProductAttributes(attributes.filter(Boolean) as unknown as Attribute[]);
        } else {
          setProductAttributes([]);
        }

        // Handle category units
        if (category.categoryUnits && category.categoryUnits.length > 0) {
          const units = category.categoryUnits
            .filter((unit) => unit.unit)
            .map((unit, order) => ({
              unitId: unit?.unit?.id,
              costPrice: 0,
              salePrice: 0,
              convertValue: unit.convertValue || 1,
              order: order + 1,
              label: unit?.unit?.name || '',
            }));
          setProductUnits([...(units.filter(Boolean) as unknown as Unit[]), ...productUnits]);
        } else {
          setProductUnits([]);
        }
      }
    },
    [categories?.data?.rows, form, isDetail, productUnits],
  );

  const handleCheckUpdte = useCallback(() => {
    if (!listVersion || listVersion.length === 0) {
      setVersion(1.1);
    } else {
      const maxVersion = Math.max(
        ...listVersion.map((item) =>
          typeof item.version === 'number' ? item.version : parseFloat(String(item.version)) || 0,
        ),
      );
      const currentMaxVersion = isFinite(maxVersion) ? maxVersion : 1.0;
      const newVersion = parseFloat((currentMaxVersion + 0.1).toFixed(1));

      setVersion(newVersion);
    }

    setInactive(false);
    setCheckUpdate(true);
    setCheckActive(true);
    setIsEdit(true);

    form.setFieldValue('version', version + 0.1);
  }, [listVersion, setVersion, setInactive, setCheckUpdate, setCheckActive, setIsEdit, form, version]);

  // Effects
  useEffect(() => {
    if (isDetail) {
      if (initialProductUnits.length > 0) {
        const unitsWithLabels = initialProductUnits.map((unit) => ({
          ...unit,
          label: unit?.unit?.name,
          checked: true,
        }));
        setProductUnits(unitsWithLabels as unknown as Unit[]);
      } else {
        setProductUnits([]);
      }
    }
  }, [isDetail, initialProductUnits]);

  useEffect(() => {
    if (isDetail && initialProductAttributes.length > 0) {
      const attributesWithLabels = initialProductAttributes.map((attr) => ({
        ...attr,
        title: attr?.attribute?.name,
        surcharge: attr?.salePrice || 0,
      }));
      setProductAttributes(attributesWithLabels as unknown as Attribute[]);
    }
  }, [isDetail, initialProductAttributes]);

  useEffect(() => {
    if (isDetail && initialProductSalePrices.length > 0) {
      setProductSalePrices(initialProductSalePrices);
    }
  }, [isDetail, initialProductSalePrices]);

  useEffect(() => {
    if (!isDetail) {
      handleCreatecode();
      handleBarcode();
    } else {
      setPrefixCode((form.getFieldValue('code') || '').substring(0, 6));
    }
  }, [isDetail, handleCreatecode, handleBarcode, form]);

  useEffect(() => {
    if (!isDetail) return;

    setCheckActive(form.getFieldValue('isActive') !== false);
    setVersion(form.getFieldValue('version'));
    // Determine edit state based on status
    const currentStatus = form.getFieldValue('status');
    setIsEdit(currentStatus === null || currentStatus === 'Draft' || currentStatus === '');
    setInactive(currentStatus === 'Inactive');

    if (Array.isArray(fieldProductImages)) setImagesData(fieldProductImages);
    setListVersion(form.getFieldValue('listVersion'));
    if (Array.isArray(fieldAttrs)) setProductAttributes(fieldAttrs);
  }, [isDetail, fieldAttrs, fieldProductImages, form]);
  useEffect(() => {
    if (isDetail && initialProductSuppliers.length > 0) {
      setProductSuppliers(initialProductSuppliers);
      form.setFieldValue('supplier', initialProductSuppliers);
    }
  }, [initialProductSuppliers, form]);
  return {
    // State
    checkActive,
    setCheckActive,
    checkInactive,
    setInactive,
    checkUpdate,
    setCheckUpdate,
    barcode,
    listVersion,
    setListVersion,
    productUnits,
    setProductUnits,
    productAttributes,
    setProductAttributes,
    productSalePrices,
    setProductSalePrices,
    productSuppliers,
    setProductSuppliers,
    modalOpen,
    setModalOpen,
    version,
    setVersion,
    isEdit,
    setIsEdit,
    statusSave,
    setStatusSave,
    imagesData,
    setImagesData,
    filteredBrands,
    prefixCode,

    // API data
    suppliers,
    brands,
    categories,
    units,
    attributes,
    code,
    isPublishId,
    isDraftId,
    isInactiveId,

    // Loading states
    isLoadingBrands,
    isLoadingCategories,

    // Handlers
    handleBarcode,
    handleCreatecode,
    handleCategoryChange,
    handleCheckUpdte,
    getBrandsByCategoryIds,
  };
};
